import type { BuyingGroupMode } from "@/hooks/api/use-account-activities";

/**
 * Interface for parameters used in buying group API calls
 */
export interface BuyingGroupApiParams {
  accountId?: string;
  opportunityIds?: string[];
  personIds?: string[];
  buyingGroupMode?: BuyingGroupMode;
  customGroupOpportunityIds?: string[];
}

/**
 * Creates a query key object for buying group related API calls
 * This ensures consistent cache key generation across all hooks
 */
export function createBuyingGroupQueryKeyPart({
  opportunityIds,
  personIds,
  buyingGroupMode = "default",
  customGroupOpportunityIds,
}: Omit<BuyingGroupApiParams, "accountId">): Record<string, unknown> {
  return {
    opportunity_ids: opportunityIds?.length ? opportunityIds : undefined,
    person_ids: personIds?.length ? personIds : undefined,
    buying_group_mode: buyingGroupMode,
    custom_group_opportunity_ids: customGroupOpportunityIds?.length
      ? customGroupOpportunityIds
      : undefined,
  };
}

/**
 * Builds request parameters for buying group API calls
 * This ensures consistent parameter handling across all hooks
 */
export function buildBuyingGroupRequestParams({
  opportunityIds,
  personIds,
  buyingGroupMode = "default",
  customGroupOpportunityIds,
}: Omit<BuyingGroupApiParams, "accountId">): Record<string, string> {
  const params: Record<string, string> = {};

  // Use explicit buying group mode parameter in all cases
  params.buying_group_mode = buyingGroupMode;

  if (buyingGroupMode === "custom") {
    // Custom mode - use both custom group opportunity IDs and person IDs when available
    if (customGroupOpportunityIds?.length) {
      params.opportunity_ids = customGroupOpportunityIds.join(",");
    }
    if (personIds?.length) {
      params.person_ids = personIds.join(",");
    }
  } else if (buyingGroupMode === "opportunity" && opportunityIds?.length) {
    // Traditional opportunity mode - send opportunity_ids
    params.opportunity_ids = opportunityIds.join(",");
  } else if (opportunityIds?.length) {
    // Default mode - no specific filtering, but include opportunity_ids if available
    params.opportunity_ids = opportunityIds.join(",");
  }

  return params;
}

/**
 * Determines if a query with buying group parameters should be enabled
 * Used to conditionally enable/disable queries based on buying group mode
 */
export function shouldEnableBuyingGroupQuery({
  accountId,
  buyingGroupMode = "default",
  customGroupOpportunityIds,
}: BuyingGroupApiParams): boolean {
  // Always need an account ID
  if (!accountId) return false;

  // In custom mode, only enable if we have opportunity IDs for some endpoints
  if (buyingGroupMode === "custom") {
    return !!customGroupOpportunityIds?.length;
  }

  // Otherwise enable as normal
  return true;
}
